'use strict';

var Constructor = require('js/vendor/constructor');
var Backbone = require('backbone');
var Api = require('js/api');
var config = require('js/config');
var _ = require('underscore');
var DataCache = require('js/mixins/dataCache');
var SyncCount = require('js/mixins/syncCount');


// ------------------------------------------------------------------------
// TODO: Tidy up pagination. We could simply always deal with pageSize/pageNumber as f/e api and then get/set methods would correctly convert that before we hit backend api (same for moreAvailable etc type helpers)
var Collection = Constructor.extendFrom(Backbone.Collection).mixes(DataCache, SyncCount);


/**
 * Hold different types of pagination as the api has different parameters
 * for different endpoints :(
 */
Collection.PAGINATION_TYPES = {
  PAGE_NUMBER: 1,
  START_LIMIT: 2,
  FROM_SIZE: 3
};


/**
 * Store API required params to fetch. Their presence will be checked prior
 * to issue a fetch request to the API
 * @type {Object}
 */
Collection.prototype.requiredParams = [];


/**
 * Store some collection options
 */
Collection.prototype.options = {};


/**
 * Store descriptive data
 */
Collection.prototype.description = {};


/**
 * Whether this collection should accumulate results by default (on api processing)
 * @type {boolean}
 */
Collection.prototype.accumulate = false;


/**
 * Count of how many a many items current described (non paged) collection
 * Ie. how many available to fetch from server
 * @type {number}
 */
Collection.prototype.total = 0;


/**
 * Is there a page beyond this described collection we can fetch from the server?
 * @type {boolean}
 */
Collection.prototype.moreAvailable = false;


/**
 * What type of pagination are we dealing with?
 * - See PAGINATION_TYPES enum
 * @type {Int}
 */
Collection.prototype.paginationType = Collection.PAGINATION_TYPES.PAGE_NUMBER;


/**
 * Data cache map
 * @type {{}}
 */
Collection.prototype.dataCacheMap = {};


/**
 * Hold parent model or null
 * @type {Model|null}
 */
Collection.prototype.parent = null;


/**
 * Initialize a collection (overridden to store options)
 */
Collection.prototype.initialize = function(models, options) {
  // Override options ref while merging defaults with user options
  this.options = _.extend({}, this.options, options);

  // Fill out defaults from options
  this.parent = this.options.parent || null;
  this.description = _.clone(this.options.description) || {};

  // Create debounced method versions
  this.debouncedFetchOneMore = _.bind(_.debounce(this.fetchOneMore, config.api.burst.wait, true), this);

  // Call super
  Collection.__super__.initialize.apply(this, arguments);

  // Initialize our data cache
  this.initializeDataCache({}, this.dataCacheMap);
};


/**
 * Override sync so we can use our own logic
 * @return {Request} Request object (promise)
 */
Collection.prototype.sync = function() {
  return Api.sync.apply(this, arguments);
};


/**
 * Wrap fetch method so it always returns a promise
 * @return {Promise} Promise for fetch action
 */
Collection.prototype.fetch = function(options) {
  // Extend options with defaults
  options = _.extend({}, this.options, options);

  //Check for required parameters before fetch
  this.requiredParams.map(function(value) {
    if (!(options.data && options.data[value]) && !(this.description && this.description[value])) {
      throw new Error('Collection ' + value + ' parameter is mandatory to fetch');
    }
  }, this);

  // Call super
  var fetch = Collection.__super__.fetch.call(this, options);

  // Ensure promise is returned
  return fetch ? fetch : Promise.broken(new Error('Fetch method rejected.'));
};


/**
 * Set data which describes the resource we are dealing with (gets sent with requests)
 * @return {this} for chaining
 */
Collection.prototype.desc = function(key, value) {
  this.description[key] = value;

  // Return this for chaining
  return this;
};


/**
 * Clear descriptive data using given key
 */
Collection.prototype.clearDesc = function(key) {
  delete this.description[key];

  // Return this for chaining
  return this;
};


/**
 * Map the models in this collection to a new instance of another collection
 * - Collection can be a new Collection to instance or existing collection to set models on
 */
Collection.prototype.mapToCollection = function(collection, parse, options) {
  // Default parse is to simply return the model and give reference to new collection
  parse = parse || function(model) {
    return model;
  };

  // Ensure we have an instance
  var collectionInstance = collection instanceof Collection ? collection : new collection();

  // Set models (passing through parser)
  collectionInstance.set(this.map(parse), options);

  return collectionInstance;
};


/**
 * Fetch the next page of a collection (accumulates by default)
 */
Collection.prototype.fetchOneMore = function(options) {
  // Modify collection description dependant on pagination type
  switch (this.paginationType) {

    case Collection.PAGINATION_TYPES.PAGE_NUMBER:
      this.desc('pageNumber', (this.description.pageNumber || 1) + 1);
      break;

    case Collection.PAGINATION_TYPES.FROM_SIZE:
      this.desc('from', (this.description.from || 0) + (this.description.size || 30));
      break;

    case Collection.PAGINATION_TYPES.START_LIMIT:
      this.desc('start', (this.description.start || 0) + (this.description.limit || 30));
      break;

  }

  return this.fetch(_.extend({ accumulate: true }, options));
};


/**
 * Default fallback collection parsing
 */
Collection.prototype.parse = function(response, options) {
  // Ensure data cache
  this.ensureDataCache(response, this.dataCacheMap);

  return this.parseCollection(response, options);
};


/**
 * Method to parse array to be set on the collection (so we can accumulate if needed)
 */
Collection.prototype.parseCollection = function(models, options) {
  // Ensure default options object exists
  options = options || {};

  // Do we want to accumulate this collection?
  if (this.accumulate || options.accumulate) {
    this.moreAvailable = this.total > (this.length + models.length);

    return this.models.concat(models);
  }

  this.moreAvailable = this.total > models.length;

  return models;
};


/**
 * Can we paginate backwards?
 */
Collection.prototype.hasPrevPage = function() {

  switch (this.paginationType) {

    case Collection.PAGINATION_TYPES.PAGE_NUMBER:
      return this.description.pageNumber > 1;

    case Collection.PAGINATION_TYPES.FROM_SIZE:
      return this.description.from > 0;

    case Collection.PAGINATION_TYPES.START_LIMIT:
      return this.description.start > 0;

  }

};


/**
 * Can we paginate forwards?
 */
Collection.prototype.hasNextPage = function() {
  switch (this.paginationType) {

    case Collection.PAGINATION_TYPES.PAGE_NUMBER:
      return this.total > (this.description.pageNumber * this.description.pageSize);

    case Collection.PAGINATION_TYPES.FROM_SIZE:
      return this.total > (this.description.from + this.description.size);

    case Collection.PAGINATION_TYPES.START_LIMIT:
      return this.total > (this.description.start + this.description.limit);

  }
};

/**
 * Return the last page number for this collections data
 */
Collection.prototype.getLastPage = function() {
  switch (this.paginationType) {

    case Collection.PAGINATION_TYPES.PAGE_NUMBER:
      return Math.ceil(this.total / this.description.pageSize);

    case Collection.PAGINATION_TYPES.FROM_SIZE:
      return Math.ceil(this.total / this.description.size);

    case Collection.PAGINATION_TYPES.START_LIMIT:
      return Math.ceil(this.total / this.description.limit);

  }
};

/**
 * Return 'parent' (a model) as data cache parent if set
 */
Collection.prototype.getDataCacheParent = function() {
  return this.parent || null;
};

/**
 * Get Ids of all the models in the collection
 */
Collection.prototype.getIds = function() {
  return this.models.map(item => {
    return item.get('id');
  });
};

module.exports = Collection;



// WEBPACK FOOTER //
// ./app/assets/js/lib/collection.js